/**
 * Field Normalization Utilities
 *
 * These functions normalize raw field values from extraction API responses
 * into standardized formats. Ported from mobile/utils/pipe-parser.ts and
 * mobile/utils/team-transformer.ts for full feature parity.
 */

import { VALIDATION_RANGES } from '../constants/validation';
import { readRosterClass } from './roster-class';

/**
 * Normalize null-like string values to "-" placeholder
 * Handles: null, undefined, "NULL", "N/A", "NA", "none", "-", empty strings
 * @param value - Raw value to normalize
 * @returns Normalized value or "-" for null-like values
 */
export function normalizeValue(value: unknown): string {
  if (value == null) return '-';
  if (typeof value !== 'string') return String(value);

  const trimmed = value.trim();
  const nullLikeValues = ['null', 'n/a', 'na', 'none', '-', ''];

  if (nullLikeValues.includes(trimmed.toLowerCase())) {
    return '-';
  }

  return trimmed || '-';
}

/**
 * Split full name into first and last name components
 * Handles multiple formats:
 * - "Last, First" → {firstName: "First", lastName: "Last"}
 * - "First Last" → {firstName: "First", lastName: "Last"}
 * - "First Middle Last" → {firstName: "First", lastName: "Last"} (drops middle)
 * - "John" → {firstName: "John", lastName: "-"}
 * @param fullName - Full name string
 * @returns Object with firstName and lastName
 */
export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  if (!fullName || fullName.trim() === '-') {
    return { firstName: '-', lastName: '-' };
  }

  const trimmed = fullName.trim();

  // Handle "Last, First" format
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((p) => p.trim());
    return {
      lastName: parts[0] || '-',
      firstName: parts[1] || '-',
    };
  }

  // Handle "First Last" or "First Middle Last" format
  const parts = trimmed.split(/\s+/);

  if (parts.length === 0) {
    return { firstName: '-', lastName: '-' };
  } else if (parts.length === 1) {
    return { firstName: parts[0], lastName: '-' };
  } else {
    // First name = first part, Last name = last part (drops middle names)
    return {
      firstName: parts[0],
      lastName: parts[parts.length - 1],
    };
  }
}

/**
 * Normalize batting hand to single uppercase character
 * @param value - Raw bats value (e.g., "R", "Right", "left", "switch")
 * @returns "R", "L", "S" (switch), or "-"
 */
export function normalizeBats(value: string): string {
  if (!value || value === '-') return '-';
  const v = value.toLowerCase().trim();
  if (v === 'r' || v === 'right') return 'R';
  if (v === 'l' || v === 'left') return 'L';
  if (v === 's' || v === 'switch' || v === 'both' || v === 'b') return 'S';
  // Return first character uppercase if unknown
  return value.charAt(0).toUpperCase() || '-';
}

/**
 * Normalize throwing hand to single uppercase character
 * @param value - Raw throws value (e.g., "R", "Right", "left")
 * @returns "R", "L", or "-"
 */
export function normalizeThrows(value: string): string {
  if (!value || value === '-') return '-';
  const v = value.toLowerCase().trim();
  if (v === 'r' || v === 'right') return 'R';
  if (v === 'l' || v === 'left') return 'L';
  // Return first character uppercase if unknown
  return value.charAt(0).toUpperCase() || '-';
}

/**
 * Split combined bats/throws value into separate components
 * Handles various separators: "/", "-", "\", ",", space
 * Examples:
 * - "R/R" → {bats: "R", throws: "R"}
 * - "L-R" → {bats: "L", throws: "R"}
 * - "Switch/Right" → {bats: "S", throws: "R"}
 * - "R" → {bats: "R", throws: "-"} (single value assumed to be bats)
 * @param value - Combined bats/throws string
 * @returns Object with normalized bats and throws
 */
export function splitBatsThrows(value: string): { bats: string; throws: string } {
  if (!value || value === '-') return { bats: '-', throws: '-' };

  // Handle various separators: /, -, \, space
  const parts = value.split(/[/\-\\,\s]+/).filter(Boolean);

  if (parts.length >= 2) {
    return {
      bats: normalizeBats(parts[0]),
      throws: normalizeThrows(parts[1]),
    };
  }

  // Single value - assume it's bats only
  return {
    bats: normalizeBats(parts[0] || '-'),
    throws: '-',
  };
}

/**
 * Normalize a roster's Class / grad-year cell to a 4-digit year string.
 *
 * Handles a stated year ("26" -> "2026", "2026" -> "2026") and every class spelling
 * roster-class.ts knows ("Freshman", "Frosh", "FR", "F", "R-Fr", "1st", "1"), which is where
 * the alias table now lives. This used to keep a second, smaller table of its own that
 * disagreed with the other two readers about redshirts; a redshirt now sits on the same rung
 * as the class it names, matching the API.
 *
 * @param value - Raw graduation year value
 * @returns 4-digit year string, or the input unchanged when it cannot be read
 */
export function normalizeGradYear(value: string): string {
  if (!value || value === '-') return '-';

  const gradYear = readRosterClass(value).gradYear;
  if (gradYear != null) return String(gradYear);

  // Couldn't parse - return as-is (better than losing data)
  return value;
}

/**
 * Normalize height to standard format "5-11"
 * Handles:
 * - "5'11" → "5-11"
 * - "5-11" → "5-11"
 * - "511" → "5-11"
 * - "5'11\"" → "5-11"
 * - "5 11" → "5-11"
 * @param value - Raw height value
 * @returns Height in "F-I" format or "-"
 */
export function normalizeHeight(value: string): string {
  if (!value || value === '-') return '-';

  // Remove quotes and extra characters
  const clean = value.replace(/['"″]/g, '').trim();

  // Already in correct format
  if (/^\d+-\d+$/.test(clean)) return clean;

  // Handle "5'11" or "5 11" format
  const match = clean.match(/(\d+)['\s-]?(\d+)/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }

  // Handle "511" format (5-11)
  if (/^\d{3}$/.test(clean)) {
    return `${clean.charAt(0)}-${clean.slice(1)}`;
  }

  return value;
}

/**
 * Normalize weight by extracting numeric value
 * Strips "lbs", "lb", or other text suffixes
 * @param value - Raw weight value (e.g., "180 lbs", "180")
 * @returns Numeric weight string or "-"
 */
export function normalizeWeight(value: string): string {
  if (!value || value === '-') return '-';

  // Extract just the number
  const match = value.match(/(\d+)/);
  return match ? match[1] : '-';
}

/**
 * Normalize phone number to standard format "XXX-XXX-XXXX"
 * Handles:
 * - 10-digit numbers: "5551234567" → "555-123-4567"
 * - 11-digit numbers starting with 1: "15551234567" → "555-123-4567"
 * - Already formatted numbers: "555-123-4567" → "555-123-4567"
 * @param value - Raw phone number
 * @returns Formatted phone number or "-"
 */
export function normalizePhone(value: string): string {
  if (!value || value === '-') return '-';

  // Extract just digits
  const digits = value.replace(/\D/g, '');

  // Valid US phone should be 10 or 11 digits
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return as-is if not a standard format
  return value;
}

/**
 * Normalize social media handle by stripping @ prefix
 * @param value - Raw handle (e.g., "@johndoe", "johndoe")
 * @returns Handle without @ or "-"
 */
export function normalizeSocialHandle(value: string): string {
  if (!value || value === '-') return '-';
  return value.replace(/^@/, '').trim();
}

/**
 * Normalize GPA and validate it's in reasonable range
 * Valid range: 0.0 to 5.0
 * @param value - Raw GPA value
 * @returns GPA string if valid, otherwise "-"
 */
export function normalizeGpa(value: string): string {
  if (!value || value === '-') return '-';

  const num = parseFloat(value);
  if (isNaN(num)) return '-';

  // Reasonable GPA range
  if (num >= VALIDATION_RANGES.gpa.min && num <= VALIDATION_RANGES.gpa.max) {
    return value;
  }

  return '-';
}
